import { useEffect, useState, useContext } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { RemoveButton } from "../form/Button";
import { GlobalVars } from "../../../state/global/globalVars";
import { AuthContext } from "../../../providers/AuthProvider";

import NavLinks from "../../../state/global/nav";
import UilBars from "@iconscout/react-unicons/icons/uil-bars";
import UilSearchAlt from "@iconscout/react-unicons/icons/uil-search-alt";
import UilSignout from "@iconscout/react-unicons/icons/uil-signout";
import Footer from "./Footer";

import logo from '../../../cityside-fiber-logo.webp';

export default function Nav({
    secondaryNavItems,
    children,
    noChildPadding,
}) {
    const desktopWidthPx = 1870;
    const mobileWidthPx = 1024;
    const [open, setOpen] = useState(false);
    const [desk, setDesktop] = useState(window.innerWidth > desktopWidthPx);
    const [mobile, setMobile] = useState(window.innerWidth < mobileWidthPx);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const { logout } = useContext(AuthContext);

    const search = (e) => {
        e.preventDefault();

        if (searchQuery.length > 0) {
            navigate(`/account/search-result?q=${searchQuery}`);
            setSearchQuery("");
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > desktopWidthPx) {
                setDesktop(true);
                setMobile(false);
                setOpen(false);
            } else if (window.innerWidth < mobileWidthPx) {
                setDesktop(false);
                setMobile(true);
            } else {
                setDesktop(false);
                setMobile(false);
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <nav className="flex flex-row z-50 h-full w-full bg-light-1 dark:bg-light-0">
            {/* Side menu */}
            <div
                className={`fixed top-0 z-50 2xl:w-auto 2xl:sticky h-full transition duration-500
					${open || desk ? "w-screen 2xl:w-auto bg-light-3/30" : "bg-transparent"}`}
                onClick={() => setOpen(false)}
            >
                <div
                    className={`flex w-[85vw] h-full bg-cityside-secondary-orange text-light-11 font-semibold sm:w-96 lg:w-60 2xl:w-[14vw]
                        lg:flex-col lg:justify-between lg:overflow-y-auto transition-[margin] duration-500
                        ${open || desk ? "m-0" : "-ml-[100vw] lg:-ml-80 -z-10"}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Page links (mobile) */}
                    <div className="flex flex-col w-full h-full py-0 lg:w-auto lg:py-0 overflow-hidden">
                        <div className="flex flex-row w-full items-center justify-between pb-0 2xl:sticky 2xl:top-0">
                            <div className="2xl:text-2xl font-bold px-4 2xl:py-3 2xl:bg-cityside-primary-blue w-full 2xl:h-14 border-light-5 text-center">
                                Project Layers
                            </div>

                            {/* Sidebar close button */}
                            <RemoveButton
                                onClick={() => setOpen(false)}
                                className="p-1 m-3 2xl:hidden bg-light-7/50"
                                isSolid={false}
                                large={true}
                            />
                        </div>
                        <div className="overflow-y-auto flex-grow">
                            <div className={`px-8 ${desk ? "pt-4" : ""}`}>
                                {/* Page links (large) */}
                                { <NavLinks mobile={false} />}
                            </div>
                        </div>
                        
                        {/* Logout button added at the bottom of sidebar */}
                        <div className="mt-auto border-t border-light-7/40 p-4">
                            <button 
                                onClick={logout}
                                className="flex items-center w-full px-4 py-3 rounded-md hover:bg-light-7/40 transition-colors"
                            >
                                <UilSignout size={GlobalVars.iconSize.md} className="mr-3" />
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`flex flex-col min-h-full w-full 2xl:w-[86vw] transition duration-500 ${open ? "blur-sm" : ""}`}>
                {/* Top menu */}
                <div className="sticky top-0 z-40">
                    {/* Main menu */}
                    <div className="grid grid-cols-2 items-center w-full 2xl:h-14 shadow-sm font-semibold text-light-11 bg-cityside-primary-blue gap-y-2 py-2 lg:flex lg:justify-between">
                        {/* Hamburger icon */}
                        <button
                            className="mx-3 lg:ml-3 px-2 2xl:hidden border border-transparent transition ease-in-out duration-350 hover:bg-light-7/40 active:bg-light-7/40 focus:outline-none focus:ring-2 focus:ring-light-7/40 h-[38px] w-[43px] rounded"
                            onClick={() => setOpen(!open)}
                        >
                            <UilBars size={ GlobalVars.iconSize.md } />
                        </button>
                        {/* Logo */}
                        <div className="pr-12 rounded flex justify-end ">
                            <img className="flex object-scale-down w-1/2" src={ logo } />
                        </div>
                        {/* Search Bar */}
                        {/* <form
                            className="flex-1 flex mx-3 mb-1 pr-8 items-center col-span-2 lg:mx-0 lg:mb-0 2xl:ml-8"
                            onSubmit={ search }
                        >
                            <input
                                type = "text"
                                className = "w-full px-4 py-2 border text-light-0 focus:border-light-2 border-light-6 focus:outline-none rounded text-sm"
                                placeholder = "Search"
                                value = { searchQuery }
                                onChange={ (e) => setSearchQuery(e.target.value) }
                            />
                            <UilSearchAlt size={ GlobalVars.iconSize.md } className="text-gray-400 -ml-8 cursor-pointer" onClick={ search } />
                        </form> */}
                    </div>
                    {/* Secondary menu (large) */}
                    {!mobile && (
                        <div className={`flex justify-evenly mx-0 ${secondaryNavItems && "px-0 pt-0 shadow-md bg-light-1 border-b border-light-1"}`}>
                            {secondaryNavItems?.map((item, index) => (
                                <NavLink
                                    to={item.path}
                                    key={index}
                                    className={({ isActive }) =>
                                        `w-full mx-0 my-0 py-2 px-4 first:border-l-0 last:border-r-0 text-center
                                        hover:text-light-11 hover:border-light-4
                                        active:bg-light-3 focus:outline-none focus:bg-light-3
                                        transition duration-350 ease-in-out
                                        ${
                                            isActive
                                            ? "font-semibold -mb-[1px] border-x border-light-1 bg-light-3 text-light-11 hover:bg-light-3"
                                            : "bg-light-4/70 text-light-10 font-normal hover:bg-light-2"
                                        }`
                                    }
									{...index === 0 && {end: true}}
                                >
                                    <span>{ item.label }</span>
                                </NavLink>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex-1 flex flex-col overflow-y-auto">
                    <main className={`flex-1 ${!noChildPadding && "p-2 sm:p-4 lg:p-8"} bg-light-10 mb-auto`}>
                        { children }
                    </main>
                    <Footer className="mt-auto"/>
                </div>
            </div>
        </nav>
    );
}
